import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from "../ui/button";
import { Card, CardContent, CardFooter } from "../ui/card";
import { authAPI } from '../../services/api';

export default function VerifyEmailPage(): React.ReactElement {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const location = useLocation();

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        // Extract token from URL parameters
        const urlParams = new URLSearchParams(location.search);
        const token = urlParams.get('token');

        if (!token) {
          setStatus('error');
          setErrorMessage('Token de vérification manquant');
          return;
        }

        console.log('🔵 [FRONTEND] Verifying token:', token);

        // Call the verify email API
        const response = await authAPI.verifyEmail(token);
        
        console.log('✅ [FRONTEND] Email verified successfully:', response.data);
        setStatus('success');

      } catch (err: any) {
        console.error('❌ [FRONTEND] Email verification failed:', err);
        
        let errorMsg = 'Erreur lors de la vérification';
        
        if (err.response && err.response.data) {
          errorMsg = err.response.data.message || err.response.data.error || errorMsg;
        }
        
        setErrorMessage(errorMsg);
        setStatus('error');
      }
    };

    verifyEmail();
  }, [location]);

  const handleReturnToSignIn = (): void => {
    window.location.href = '/signin';
  };

  const handleResendVerification = (): void => {
    // You can implement this if needed
    window.location.href = '/signup';
  };

  if (status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-accent py-12 px-4 sm:px-6 lg:px-8 dark:bg-gray-900">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center">
            <h1 className="mt-6 text-3xl font-bold font-serif">Vérification en cours</h1>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Veuillez patienter pendant que nous vérifions votre email...
            </p>
          </div>

          <Card>
            <CardContent className="pt-6">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 mx-auto bg-blue-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-blue-600 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                  </svg>
                </div>
                <h3 className="text-lg font-medium">Vérification de votre email...</h3>
                <p className="text-sm text-gray-600">
                  Cette opération ne prend que quelques instants.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-accent py-12 px-4 sm:px-6 lg:px-8 dark:bg-gray-900">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center">
            <h1 className="mt-6 text-3xl font-bold font-serif">Erreur de vérification</h1>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Un problème est survenu lors de la vérification
            </p>
          </div>

          <Card>
            <CardContent className="pt-6">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </div>
                <h3 className="text-lg font-medium">Vérification échouée</h3>
                <p className="text-sm text-gray-600">
                  {errorMessage}
                </p>
                <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
                  {errorMessage}
                </div>
              </div>
            </CardContent>

            <CardFooter className="flex flex-col space-y-4">
              <Button 
                onClick={handleReturnToSignIn}
                className="w-full"
                variant="outline"
                type="button"
              >
                Retour à la connexion
              </Button>
              <Button 
                onClick={handleResendVerification}
                className="w-full"
                type="button"
              >
                Créer un nouveau compte
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    );
  }

  // Success state
  return (
    <div className="flex min-h-screen items-center justify-center bg-accent py-12 px-4 sm:px-6 lg:px-8 dark:bg-gray-900">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <h1 className="mt-6 text-3xl font-bold font-serif">Email vérifié</h1>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Votre compte a été activé avec succès
          </p>
        </div>

        <Card>
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
              <h3 className="text-lg font-medium">Email vérifié avec succès !</h3>
              <p className="text-sm text-gray-600">
                Votre adresse email a été confirmée et votre compte est maintenant activé.
                Vous pouvez désormais vous connecter et accéder à toutes nos ressources.
              </p>
            </div>
          </CardContent>

          <CardFooter className="flex flex-col space-y-4">
            <Button 
              onClick={handleReturnToSignIn}
              className="w-full"
              type="button"
            >
              Se connecter
            </Button>
            <p className="text-center text-sm text-gray-500">
              Bienvenue dans notre communauté !
            </p>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}